import { IsoPoint, Transform, ConnectionPoint, DrawingElement, LineElement } from '@/app/types/cad';
import { isoToScreen } from '@/app/utils/isometric';

/**
 * Apply transform to a single IsoPoint
 */
export function transformPoint(point: IsoPoint, transform: Transform): IsoPoint {
  let u = point.u;
  let v = point.v;

  // Apply rotation (around origin)
  const rad = (transform.rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  
  const rotatedU = u * cos - v * sin;
  const rotatedV = u * sin + v * cos;
  
  u = rotatedU;
  v = rotatedV;

  // Apply mirror
  if (transform.mirrorX) u = -u;
  if (transform.mirrorY) v = -v;

  // Apply scale
  u *= transform.scale;
  v *= transform.scale;

  // Apply translation
  u += transform.position.u;
  v += transform.position.v;

  return { u, v };
}

/**
 * Get all connection points from a block with transform applied
 */
export function getTransformedSockets(
  elements: DrawingElement[],
  transform: Transform
): Array<{ index: number; position: IsoPoint; element: ConnectionPoint }> {
  const connections = elements.filter(
    (el): el is ConnectionPoint => el.type === 'connection'
  );

  return connections.map((conn, index) => ({
    index,
    position: transformPoint(conn.position, transform),
    element: conn,
  }));
}

/**
 * Get all line endpoints from a block with transform applied
 */
export function getTransformedLineEndpoints(
  elements: DrawingElement[],
  transform: Transform
): Array<{ 
  index: number; 
  startPosition: IsoPoint; 
  endPosition: IsoPoint; 
  element: LineElement;
}> {
  const lines = elements.filter(
    (el): el is LineElement => el.type === 'line'
  );

  return lines.map((line, index) => ({
    index,
    startPosition: transformPoint(line.start, transform),
    endPosition: transformPoint(line.end, transform),
    element: line,
  }));
}

/**
 * Calculate distance between two IsoPoints
 */
export function isoDistance(a: IsoPoint, b: IsoPoint): number {
  const du = a.u - b.u;
  const dv = a.v - b.v;
  return Math.sqrt(du * du + dv * dv);
}

/**
 * Snap position to nearest socket if within threshold
 */
export function snapToSocket(
  position: IsoPoint,
  allSockets: Array<{ blockId: string; socketIndex: number; position: IsoPoint }>,
  threshold: number = 0.5 // Grid units
): { snapped: boolean; position: IsoPoint; target?: { blockId: string; socketIndex: number } } {
  let closestDistance = threshold;
  let closestSocket: { blockId: string; socketIndex: number; position: IsoPoint } | null = null;

  for (const socket of allSockets) {
    const dist = isoDistance(position, socket.position);
    if (dist < closestDistance) {
      closestDistance = dist;
      closestSocket = socket;
    }
  }

  if (closestSocket) {
    return {
      snapped: true,
      position: closestSocket.position,
      target: {
        blockId: closestSocket.blockId,
        socketIndex: closestSocket.socketIndex,
      },
    };
  }

  return { snapped: false, position };
}

/**
 * Snap position to nearest line endpoint if within threshold
 */
export function snapToLineEndpoint(
  position: IsoPoint,
  allLineEndpoints: Array<{ 
    blockId: string; 
    lineIndex: number; 
    isStart: boolean; // true for start, false for end
    position: IsoPoint;
  }>,
  threshold: number = 0.5 // Grid units
): { 
  snapped: boolean; 
  position: IsoPoint; 
  target?: { 
    blockId: string; 
    lineIndex: number; 
    isStart: boolean;
  } 
} {
  let closestDistance = threshold;
  let closestEndpoint: typeof allLineEndpoints[0] | null = null;

  for (const endpoint of allLineEndpoints) {
    const dist = isoDistance(position, endpoint.position);
    if (dist < closestDistance) {
      closestDistance = dist;
      closestEndpoint = endpoint;
    }
  }

  if (closestEndpoint) {
    return {
      snapped: true,
      position: closestEndpoint.position,
      target: {
        blockId: closestEndpoint.blockId,
        lineIndex: closestEndpoint.lineIndex,
        isStart: closestEndpoint.isStart,
      },
    };
  }

  return { snapped: false, position };
}

/**
 * Snap position to nearest connection point (socket OR line endpoint)
 */
export function snapToAnyConnection(
  position: IsoPoint,
  allSockets: Array<{ blockId: string; socketIndex: number; position: IsoPoint }>,
  allLineEndpoints: Array<{ 
    blockId: string; 
    lineIndex: number; 
    isStart: boolean;
    position: IsoPoint;
  }>,
  threshold: number = 0.5
): { 
  snapped: boolean; 
  position: IsoPoint; 
  target?: { 
    type: 'socket' | 'line';
    blockId: string; 
    socketIndex?: number;
    lineIndex?: number;
    isStart?: boolean;
  } 
} {
  // Try socket snap first
  const socketSnap = snapToSocket(position, allSockets, threshold);
  
  // Try line endpoint snap
  const lineSnap = snapToLineEndpoint(position, allLineEndpoints, threshold);

  // Choose the closest one
  let closestDist = threshold;
  let bestSnap: typeof socketSnap | typeof lineSnap | null = null;
  let snapType: 'socket' | 'line' | null = null;

  if (socketSnap.snapped) {
    closestDist = isoDistance(position, socketSnap.position);
    bestSnap = socketSnap;
    snapType = 'socket';
  }

  if (lineSnap.snapped) {
    const lineDist = isoDistance(position, lineSnap.position);
    if (lineDist < closestDist) {
      closestDist = lineDist;
      bestSnap = lineSnap;
      snapType = 'line';
    }
  }

  if (bestSnap && snapType) {
    if (snapType === 'socket' && bestSnap.target && 'socketIndex' in bestSnap.target) {
      return {
        snapped: true,
        position: bestSnap.position,
        target: {
          type: 'socket',
          blockId: bestSnap.target.blockId,
          socketIndex: bestSnap.target.socketIndex,
        },
      };
    } else if (snapType === 'line' && bestSnap.target && 'lineIndex' in bestSnap.target) {
      return {
        snapped: true,
        position: bestSnap.position,
        target: {
          type: 'line',
          blockId: bestSnap.target.blockId,
          lineIndex: bestSnap.target.lineIndex,
          isStart: bestSnap.target.isStart,
        },
      };
    }
  }

  return { snapped: false, position };
}

/**
 * Generate a random HSL color for system
 */
let colorHue = 0;
export function generateSystemColor(): string {
  const hue = colorHue % 360;
  colorHue += 137.5; // Golden angle for good distribution
  return `hsl(${hue}, 70%, 50%)`;
}

/**
 * Create default transform
 */
export function createDefaultTransform(position: IsoPoint = { u: 0, v: 0 }): Transform {
  return {
    position,
    rotation: 0,
    scale: 1.0,
    mirrorX: false,
    mirrorY: false,
  };
}

/**
 * Rotate transform by 90 degrees
 */
export function rotateTransform(transform: Transform): Transform {
  return {
    ...transform,
    rotation: (transform.rotation + 90) % 360,
  };
}